"use strict";
import { el, round, esc, srTable } from "../util";
export function renderDumbbell(host, data) {
  host.innerHTML = "";
  host.classList.add("db-wrap");
  const total = data.survey_total || data.groups.reduce((s, g) => s + (g.survey_count || 0), 0);
  const rows = data.groups.map((g) => {
    const sp = g.survey_pct != null ? g.survey_pct : total ? 100 * (g.survey_count || 0) / total : 0;
    return { label: g.label, survey: sp, baseline: g.baseline_pct || 0, gap: sp - (g.baseline_pct || 0) };
  });
  const max = data.axis_max || Math.ceil(Math.max.apply(null, rows.map((r) => Math.max(r.survey, r.baseline))) / 10) * 10;
  const hd = rows.slice().sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap)).slice(0, 2);
  const headline = hd.map((r) => {
    const col = r.gap >= 0 ? "var(--db-over)" : "var(--db-under)";
    const sign = r.gap >= 0 ? "+" : "\u2212";
    return `<span class="db-pill"><span>${esc(r.label)}</span><b style="color:${col}">${sign}${Math.abs(round(r.gap))}</b></span>`;
  }).join("");
  if (data.title) host.insertAdjacentHTML("beforeend", `<h2 class="db-head">${esc(data.title)}</h2>`);
  if (data.subtitle) host.insertAdjacentHTML("beforeend", `<p class="db-sub">${esc(data.subtitle)}</p>`);
  host.insertAdjacentHTML("beforeend", `<div class="db-headline">${headline}</div>`);
  const W = 720;
  const LAB = 210;
  const GUT = 42;
  const R = 40;
  const TOP = 14;
  const x0 = LAB;
  const xr = W - GUT;
  const pw = xr - x0;
  const H = TOP + rows.length * R + 44;
  const X = (v) => x0 + v / max * pw;
  const svg = el("svg", { viewBox: `0 0 ${W} ${H}`, class: "db-svg", role: "img" });
  svg.setAttribute(
    "aria-label",
    `${data.title}. ` + rows.map((r) => `${r.label}: events ${round(r.survey)}%, students ${round(r.baseline)}%`).join("; ")
  );
  const step = max > 40 ? 10 : max > 20 ? 5 : 2;
  for (let v = 0; v <= max + 1e-3; v += step) {
    const gx = round(X(v), 1);
    svg.appendChild(el("line", { x1: gx, y1: TOP - 2, x2: gx, y2: TOP + rows.length * R - 6, class: "db-grid" }));
    const t = el("text", { x: gx, y: TOP + rows.length * R + 10, class: "db-tick", "text-anchor": "middle" });
    t.textContent = `${v}%`;
    svg.appendChild(t);
  }
  const tip = document.createElement("div");
  tip.className = "db-tip";
  function showTip(r, xs, cy) {
    const b = host.getBoundingClientRect();
    const sb = svg.getBoundingClientRect();
    const sc = sb.width / W;
    tip.innerHTML = `<div class="tt-lab">${esc(r.label)}</div><div class="tt-row"><span>MSEG Respondents</span><span>${round(r.survey, 1)}%</span></div><div class="tt-row"><span>OUSD students</span><span>${round(r.baseline, 1)}%</span></div><div class="tt-row"><span>Our Gap</span><span>${r.gap >= 0 ? "+" : "\u2212"}${Math.abs(round(r.gap, 1))} pts</span></div>`;
    tip.style.left = `${sb.left - b.left + xs * sc}px`;
    tip.style.top = `${sb.top - b.top + cy * sc - 12}px`;
    tip.style.opacity = "1";
  }
  function hideTip() {
    tip.style.opacity = "0";
  }
  rows.forEach((r, i) => {
    const cy = TOP + i * R + R / 2;
    const xs = round(X(r.survey), 1);
    const xb = round(X(r.baseline), 1);
    const over = r.gap >= 0;
    const col = over ? "var(--db-over)" : "var(--db-under)";
    const g = el("g", {
      class: "db-row",
      tabindex: "0",
      "aria-label": `${r.label}, MSEG respondents ${round(r.survey)} percent, OUSD students ${round(r.baseline)} percent`
    });
    g.style.setProperty("--d", `${i * 70}ms`);
    g.appendChild(el("rect", { x: 0, y: cy - R / 2, width: W, height: R, class: "focusring", rx: 6 }));
    const lab = el("text", { x: 4, y: cy, class: "lab", "dominant-baseline": "middle" });
    lab.textContent = r.label;
    g.appendChild(lab);
    const lo = Math.min(xs, xb);
    const hi = Math.max(xs, xb);
    const conn = el("line", {
      x1: lo,
      y1: cy,
      x2: hi,
      y2: cy,
      class: "db-conn",
      stroke: Math.abs(r.gap) < 0.05 ? "var(--db-grid)" : col
    });
    conn.style.setProperty("--len", `${hi - lo}px`);
    g.appendChild(conn);
    g.appendChild(el("circle", { cx: xb, cy, r: 6, class: "db-dot-base" }));
    g.appendChild(el("circle", { cx: xs, cy, r: 6, class: "db-dot-survey" }));
    const sign = r.gap >= 0 ? "+" : "\u2212";
    const gapTxt = Math.abs(round(r.gap)) < 1 && r.gap !== 0 ? "\xB1<1" : sign + Math.abs(round(r.gap));
    const gt = el("text", {
      x: W - 2,
      y: cy,
      class: "gap",
      "text-anchor": "end",
      "dominant-baseline": "middle",
      fill: Math.abs(round(r.gap)) === 0 ? "var(--db-faint)" : col
    });
    gt.textContent = Math.abs(round(r.gap)) === 0 ? "0" : gapTxt;
    g.appendChild(gt);
    ["mouseenter", "focus"].forEach((ev) => g.addEventListener(ev, () => showTip(r, xs, cy)));
    ["mouseleave", "blur"].forEach((ev) => g.addEventListener(ev, hideTip));
    svg.appendChild(g);
  });
  host.appendChild(svg);
  host.insertAdjacentHTML(
    "beforeend",
    `<div class="db-legend"><span class="k"><svg width="14" height="14"><circle cx="7" cy="7" r="6" fill="var(--db-survey)"/></svg>MSEG Respondents (n=${total})</span><span class="k"><svg width="14" height="14"><circle cx="7" cy="7" r="6" fill="var(--db-baseline)" stroke="var(--db-baseline-stroke)" stroke-width="2"/></svg>OUSD students</span><span class="k">line = gap in percentage points</span></div>`
  );
  if (data.source) {
    host.insertAdjacentHTML(
      "beforeend",
      `<p class="db-src">${esc(data.source)}${data.updated ? ` \xB7 updated ${esc(data.updated)}` : ""}</p>`
    );
  }
  host.appendChild(tip);
  host.insertAdjacentHTML(
    "beforeend",
    srTable(
      data.title || "Representation by group",
      ["Group", "MSEG Respondents %", "OUSD students %", "Our Gap (pts)"],
      rows.map((r) => [
        r.label,
        round(r.survey, 1),
        round(r.baseline, 1),
        (r.gap >= 0 ? "+" : "\u2212") + Math.abs(round(r.gap, 1))
      ])
    )
  );
  requestAnimationFrame(() => svg.classList.add("db-anim"));
}
